import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { BeneficiaryContactCreatedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactCreated';
import type { BeneficiaryContactDeletedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactDeleted';
import type { BeneficiaryContactUpdatedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactUpdated';
import type { BeneficiaryContactsFetchedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactsFetched';
import BeneficiaryContactsListView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactsListView';
import BeneficiaryContactView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type BeneficiaryContactsStore = {
	contacts: BeneficiaryContactsListView | null;
};

const initialState: BeneficiaryContactsStore = {
	contacts: null,
};

type Actions =
	| BeneficiaryContactsFetchedAction
	| BeneficiaryContactCreatedAction
	| BeneficiaryContactUpdatedAction
	| BeneficiaryContactDeletedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenBeneficiaryContactsFetched = (
	store: BeneficiaryContactsStore,
	action: BeneficiaryContactsFetchedAction,
): BeneficiaryContactsStore => ({
	...store,
	contacts: new BeneficiaryContactsListView(
		action.payload.results.map((contact) => new BeneficiaryContactView(contact)),
		action.beneficiaryId,
		action.payload.totalRecords,
	),
});

// Ownership checked before mutating (`guides/state-stores.md`): a stale response for a beneficiary the
// screen has since navigated away from must never overwrite the list currently displayed.
const whenBeneficiaryContactCreated = (
	store: BeneficiaryContactsStore,
	action: BeneficiaryContactCreatedAction,
): BeneficiaryContactsStore => {
	if (store.contacts === null || store.contacts.beneficiaryId !== action.beneficiaryId) {
		return store;
	}

	return {
		...store,
		contacts: store.contacts.addContact(new BeneficiaryContactView(action.payload)),
	};
};

const whenBeneficiaryContactUpdated = (
	store: BeneficiaryContactsStore,
	action: BeneficiaryContactUpdatedAction,
): BeneficiaryContactsStore => {
	if (store.contacts === null || store.contacts.beneficiaryId !== action.beneficiaryId) {
		return store;
	}

	return {
		...store,
		contacts: store.contacts.updateContact(new BeneficiaryContactView(action.payload)),
	};
};

const whenBeneficiaryContactDeleted = (
	store: BeneficiaryContactsStore,
	action: BeneficiaryContactDeletedAction,
): BeneficiaryContactsStore => {
	if (store.contacts === null || store.contacts.beneficiaryId !== action.beneficiaryId) {
		return store;
	}

	return {
		...store,
		contacts: store.contacts.removeContact(action.payload.contactId),
	};
};

const whenSubjectLeft = (
	store: BeneficiaryContactsStore,
	action: SubjectLeftAction,
): BeneficiaryContactsStore => {
	const { typeKey, subjectId } = action.payload;

	if (typeKey !== BENEFICIARY_SUBJECT_TYPE_KEY || store.contacts?.beneficiaryId !== subjectId) {
		return store;
	}

	return { ...store, contacts: null };
};

const whenUserRevoked = (): BeneficiaryContactsStore => initialState;

export default registerStore<'beneficiaryContactsStore', Actions>(
	'beneficiaryContactsStore',
	createSliceReducer({
		BENEFICIARY_CONTACTS_FETCHED: whenBeneficiaryContactsFetched,
		BENEFICIARY_CONTACT_CREATED: whenBeneficiaryContactCreated,
		BENEFICIARY_CONTACT_UPDATED: whenBeneficiaryContactUpdated,
		BENEFICIARY_CONTACT_DELETED: whenBeneficiaryContactDeleted,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
